import LandingSection from '@/components/landing/LandingSection'
import JsonLd from '@/components/JsonLd'
import {
  getBreadcrumbSchema,
  getSoftwareApplicationSchema,
} from '@/utils/schemas'
import Link from 'next/link'
import {
  FileText,
  MessageSquare,
  Camera,
  Download,
  Search,
  Share2,
} from 'react-feather'

const breadcrumbs = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
]

export default function About() {
  return (
    <LandingSection
      title="About"
      description="Annotate.tv is a video annotation tool for YouTube that helps you take timestamped notes, chat with videos using AI, and export to your favorite apps."
      canonical="/about"
    >
      <JsonLd
        data={[
          getSoftwareApplicationSchema(),
          getBreadcrumbSchema(breadcrumbs),
        ]}
      />

      {/* Hero section with entity definition */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
              About Annotate.tv
            </h1>
            <div className="mt-8 prose prose-lg mx-auto text-gray-600">
              <p className="text-xl leading-relaxed">
                <strong>Annotate.tv is a video annotation tool</strong> designed
                to help learners, educators, and content creators get more value
                from YouTube videos. It enables users to take timestamped notes
                that sync with video playback, making it easy to capture and
                review key moments.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Key features section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            What Annotate.tv Does
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<FileText className="h-8 w-8" />}
              title="Timestamped Notes"
              description="Take notes that automatically sync with the video timestamp. Click any note to jump directly to that moment in the video."
            />
            <FeatureCard
              icon={<MessageSquare className="h-8 w-8" />}
              title="AI Chat"
              description="Ask questions about the video and get instant answers based on the transcript. No more rewinding to find information."
            />
            <FeatureCard
              icon={<Camera className="h-8 w-8" />}
              title="Text Extraction (OCR)"
              description="Extract text directly from video slides and frames with one click. Perfect for capturing presentation content."
            />
            <FeatureCard
              icon={<Download className="h-8 w-8" />}
              title="Export Options"
              description="Export notes to PDF, Markdown (Notion, Evernote, Obsidian), or sync directly with Readwise."
            />
            <FeatureCard
              icon={<Search className="h-8 w-8" />}
              title="Search"
              description="Search across all your notes and videos to instantly find what you're looking for."
            />
            <FeatureCard
              icon={<Share2 className="h-8 w-8" />}
              title="Share"
              description="Create public links to share annotated videos with students, colleagues, or the world."
            />
          </div>
        </div>
      </div>

      {/* Pricing summary */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Simple Pricing
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-xl font-bold text-gray-900">Free Starter</h3>
              <p className="text-4xl font-extrabold text-primary mt-2">$0</p>
              <p className="text-gray-600 mt-2">Up to 5 videos</p>
              <p className="text-gray-600">Unlimited notes</p>
              <p className="text-gray-600">Playlists and sharing</p>
            </div>
            <div className="bg-secondary bg-opacity-5 border-2 border-primary rounded-lg p-6 text-center">
              <h3 className="text-xl font-bold text-gray-900">Premium</h3>
              <p className="text-4xl font-extrabold text-primary mt-2">
                $5<span className="text-lg text-gray-600">/mo</span>
              </p>
              <p className="text-gray-600 mt-2">Unlimited videos</p>
              <p className="text-gray-600">AI chat, OCR, transcripts</p>
              <p className="text-gray-600">All export options</p>
            </div>
          </div>
        </div>
      </div>

      {/* Use cases */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Who Uses Annotate.tv
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Students & Learners
              </h3>
              <p className="text-gray-600">
                Take effective notes on educational videos, lectures, and
                tutorials. Active note-taking improves comprehension and
                retention of material.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Educators & Teachers
              </h3>
              <p className="text-gray-600">
                Create annotated videos for students. Link to resources, add
                context, and share supplementary materials without re-recording.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Researchers
              </h3>
              <p className="text-gray-600">
                Analyze video content systematically. Extract insights, organize
                findings, and export to knowledge management tools.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Content Creators
              </h3>
              <p className="text-gray-600">
                Research and take notes on videos in your niche. Extract ideas
                and organize content for your own productions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to optimize your learning?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Start taking notes on YouTube videos for free
          </p>
          <Link href="/signup">
            <a className="inline-block bg-primary text-white px-8 py-4 rounded-lg text-lg font-medium clickable-opacity">
              Get Started Free
            </a>
          </Link>
        </div>
      </div>
    </LandingSection>
  )
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="text-primary mb-4">{icon}</div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  )
}
